import {
  BookkeepingActionFilter as BookkeepingActionFilterProto,
  BookkeepingPendingAction as BookkeepingPendingActionProto,
  Assessment as BookkeepingAssessmentProto,
  ConfirmManagedPlatformAccessRequest,
  GetAssessmentRequest,
  GetBookkeepingPendingActionAggregationsRequest,
  ListBookkeepingPendingActionsRequest,
  CompleteBookkeepingAssessmentReviewRequest,
} from '@taxfyle/api-internal/internal/bookkeeping_pb'
import { collection } from 'infra/collection'
import BookkeepingPendingAction, {
  BookkeepingManagerAction,
} from './bookkeeping/BookkeepingPendingAction'
import { BookkeepingManagerAPI } from './bookkeeping/API'
import { PrimeMemberByPublicId } from 'iam/MemberStore'
import { Id } from 'types/definitions'
import BookkeepingAssessment from './bookkeeping/BookkeepingAssessment'
import { map } from 'rxjs'

export interface SearchBookkeepingPendingActionRequest {
  workspaceIds?: string[]
  orderBy?: string
  pageSize?: number
  pageToken?: string
  searchText?: string
  action?: BookkeepingManagerAction
}

export interface BookkeepingPendingActionAggregations {
  total: number
  confirmAccessTotal: number
  assessmentReviewTotal: number
}

export default class BookkeepingManagerStore {
  /**
   * Bookkeeping events from the realtime channel.
   */
  readonly events$ = this.bookkeepingManagerAPI.events$.pipe(
    map((x) => x.getBookkeepingUpdated()?.toObject().progressionInfo?.id),
  )

  /**
   * Bookkeeping pending actions collection.
   */
  bookkeepingPendingActions = collection<
    BookkeepingPendingActionProto.AsObject,
    BookkeepingPendingAction
  >({
    create: () => new BookkeepingPendingAction(this.primeMember),
  })

  /**
   * Bookkeeping assessments collection.
   */
  bookkeepingAssessments = collection<
    BookkeepingAssessmentProto.AsObject,
    BookkeepingAssessment
  >({
    create: () => new BookkeepingAssessment(),
    getDataId: (x) => x.jobId,
  })

  constructor(
    private bookkeepingManagerAPI: BookkeepingManagerAPI,
    private primeMember: PrimeMemberByPublicId,
  ) {}

  /**
   * Searches bookkeeping pending actions.
   *
   * @param query
   * @returns
   */
  async searchBookkeepingPendingActions(
    query: SearchBookkeepingPendingActionRequest,
  ) {
    const request = new ListBookkeepingPendingActionsRequest()

    if (query.workspaceIds) {
      request.setWorkspaceIdsList(query.workspaceIds)
    }

    if (query.orderBy) {
      request.setOrderBy(query.orderBy)
    }

    if (query.pageSize) {
      request.setPageSize(query.pageSize)
    }

    if (query.pageToken) {
      request.setPageToken(query.pageToken)
    }

    switch (query.action) {
      case BookkeepingManagerAction.ConfirmAccess:
        request.setActionFilter(BookkeepingActionFilterProto.CONFIRM_ACCESS)
        break
      case BookkeepingManagerAction.AssessmentReview:
        request.setActionFilter(BookkeepingActionFilterProto.ASSESSMENT_REVIEW)
        break
      default:
        request.setActionFilter(BookkeepingActionFilterProto.ALL)
    }

    if (query.searchText) {
      request.setSearchText(query.searchText)
    }

    const result =
      await this.bookkeepingManagerAPI.listBookkeepingPendingActions(request)

    return {
      data: this.bookkeepingPendingActions.set(
        result.getBookkeepingPendingActionsList().map((op) => op.toObject()),
      ),
      total: result.getBookkeepingPendingActionsList().length,
      cursor: result.getNextPageToken(),
    }
  }

  /**
   * Gets bookkeeping pending action aggregations.
   *
   * @param workspaceIds
   * @returns
   */
  async getBookkeepingPendingActionAggregations(
    workspaceIds?: string[],
  ): Promise<BookkeepingPendingActionAggregations> {
    const request = new GetBookkeepingPendingActionAggregationsRequest()

    if (workspaceIds) {
      request.setWorkspaceIdsList(workspaceIds)
    }

    const resp = await this.bookkeepingManagerAPI
      .getBookkeepingPendingActionAggregations(request)
      .then((x) => x.toObject())

    return {
      total: resp.total,
      confirmAccessTotal: resp.confirmAccessTotal,
      assessmentReviewTotal: resp.assessmentReviewTotal,
    }
  }

  /**
   * Confirms managed platform access.
   *
   * @returns
   */
  async confirmManagedPlatformAccess(id: Id): Promise<void> {
    const assertedId = Id.assert(id)
    const request = new ConfirmManagedPlatformAccessRequest().setId(assertedId)

    await this.bookkeepingManagerAPI.confirmManagedPlatformAccess(request)

    // Remove it from the list because the assessment review status is review_requested.
    this.bookkeepingPendingActions.remove(assertedId)
    this.bookkeepingAssessments.remove(assertedId)
  }

  /**
   * Fetches bookkeeping assessment.
   *
   * @returns
   */
  async fetchAssessment(id: Id) {
    const request = new GetAssessmentRequest().setJobId(Id.assert(id))

    return this.bookkeepingManagerAPI
      .getAssessment(request)
      .then((x) => x.toObject())
      .then(this.bookkeepingAssessments.set)
  }

  /**
   * Confirms bookkeeping assessment review.
   *
   * @param request
   * @returns
   */
  async completeBookkeepingAssessmentReview(id: Id) {
    const assertedId = Id.assert(id)
    const request = new CompleteBookkeepingAssessmentReviewRequest().setId(
      assertedId,
    )

    await this.bookkeepingManagerAPI.completeBookkeepingAssessmentReview(
      request,
    )

    this.bookkeepingPendingActions.remove(assertedId)
    this.bookkeepingAssessments.remove(assertedId)
  }
}
